import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { UPLOAD_STATUS } from "../types";
import { uploadReferralFileToAiOcr, uploadResultFileToAiOcr } from "services/aiOcr";
import FileItem from "../FileItem";

const Uploader = ({ type }) => {
  const [uploadedFiles, setUploadedFiles] = useState({});

  const uploadFileToStorage = async (file, originFileList) => {
    const { id, fileData } = file;

    try {
      // Set uploading status
      originFileList[id] = {
        ...originFileList[id],
        status: UPLOAD_STATUS.running,
      };
      setUploadedFiles({
        ...originFileList,
        ...uploadedFiles,
      });

      // Upload file to API
      let response;
      switch (type) {
        case "result":
          response = await uploadResultFileToAiOcr(fileData);
          break;
        case "referral":
          response = await uploadReferralFileToAiOcr(fileData);
          break;
        default:
          response = await uploadReferralFileToAiOcr(fileData);
      }

      if (response && response.data) {
        // Success
        originFileList[id] = {
          ...originFileList[id],
          status: UPLOAD_STATUS.success,
          taskId: response.data.taskId,
          fileUrl: response.data.fileUrl,
          processingId: response.data.processingId,
          createdAt: response.data.createdAt,
        };
      } else {
        // API returned null/error
        originFileList[id] = {
          ...originFileList[id],
          status: UPLOAD_STATUS.error,
          error: new Error("Upload failed"),
        };
      }
    } catch (error) {
      // Exception occurred
      originFileList[id] = {
        ...originFileList[id],
        status: UPLOAD_STATUS.error,
        error,
      };
    }

    setUploadedFiles({
      ...originFileList,
      ...uploadedFiles,
    });
  };

  const retry = async (file) => {
    const { id, fileData } = file;

    try {
      // Set uploading status
      setUploadedFiles({
        ...uploadedFiles,
        [id]: {
          ...uploadedFiles[id],
          status: UPLOAD_STATUS.running,
          error: undefined,
        },
      });

      // Upload file to API
      let response;
      switch (type) {
        case "result":
          response = await uploadResultFileToAiOcr(fileData);
          break;
        case "referral":
          response = await uploadReferralFileToAiOcr(fileData);
          break;
        default:
          response = await uploadReferralFileToAiOcr(fileData);
      }

      if (response && response.data) {
        // Success
        setUploadedFiles({
          ...uploadedFiles,
          [id]: {
            ...uploadedFiles[id],
            status: UPLOAD_STATUS.success,
            taskId: response.data.taskId,
            fileUrl: response.data.fileUrl,
            processingId: response.data.processingId,
            createdAt: response.data.createdAt,
          },
        });
      } else {
        // API returned null/error
        setUploadedFiles({
          ...uploadedFiles,
          [id]: {
            ...uploadedFiles[id],
            status: UPLOAD_STATUS.error,
            error: new Error("Upload failed"),
          },
        });
      }
    } catch (error) {
      // Exception occurred
      setUploadedFiles({
        ...uploadedFiles,
        [id]: {
          ...uploadedFiles[id],
          status: UPLOAD_STATUS.error,
          error,
        },
      });
    }
  };

  const formatFile = (file) => {
    const { name, lastModified } = file;
    const id = uuidv4();
    const timestamp = moment().unix();
    const fileName = `${timestamp}-${name}`;
    return { name, lastModified, id, status: UPLOAD_STATUS.default, timestamp, fileName };
  };

  const handleFileEvent = (e) => {
    const files = e.target.files;
    const fileArray = Array.from(files);
    const formattedFiles = fileArray.reduce((result, file) => {
      const formattedFile = formatFile(file);
      return {
        ...result,
        [formattedFile.id]: {
          ...formattedFile,
          fileData: file,
        },
      };
    }, {});

    Object.values(formattedFiles).forEach(async (file) => {
      uploadFileToStorage(file, formattedFiles);
    });
  };

  return (
    <div className="uploader-container mt-2">
      <label htmlFor="fileUpload">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a className="button is-primary">CHOOSE FILES</a>
      </label>
      <input
        className="upload-input"
        id="fileUpload"
        onChange={handleFileEvent}
        type="file"
        multiple
        accept="application/pdf, image/png"
        onInput={(e) => {
          if (e.target.files.length > 10) {
            alert("You can only upload a maximum of 10 files");
            e.target.value = null; // Clear the input
          }
        }}
      />
      <div className="uploader-list">
        {Object.values(uploadedFiles).map((file) => (
          <FileItem file={file} key={file.id} retry={retry} />
        ))}
      </div>
    </div>
  );
};

export default Uploader;
